import { ClampJoint, CuttingMaterial, recalculatePanelJoint } from 'cloud-shared-lib';
import _ from 'lodash';
import { Countertop, CountertopLine, CountertopPanel } from './countertop.interface';

export enum PanelLengthChangeDirection {
  None,
  Left,
  Right,
  LeftRight,
}

export class CountertopUtils {
  /**
   * Проверяет, можно ли задать такую ширину в соответствии с параметрами материала.
   * Создает исключение, если ширина не подходит под параметры материала.
   * @param value Ширина которую необходимо проверить
   * @param material Материал для которого необходимо проверить ширину
   */
  static checkLineWidth(value: number, material: CuttingMaterial): void {
    if (value > material.width) {
      throw new Error($localize`Ширина столешницы не должна превышать ширину материала.`);
    }
  }

  static checkPanelLength(value: number, material: CuttingMaterial): void {
    if (value > material.height) {
      throw new Error($localize`Длина панели столешницы не должна превышать высоту материала.`);
    }
  }

  static setLineLength(
    value: number,
    line: CountertopLine,
    createPanel: (length: number, width: number) => CountertopPanel
  ): void {
    const maxPanelLength = line.material.height;
    const currentLineLength = line.length;

    if (currentLineLength < value) {
      const lastPanelLength = _.last(line.panels).length;
      const lineLengthWithoutLastPanel = currentLineLength - lastPanelLength;
      let newLastPanelLength = value - lineLengthWithoutLastPanel;

      _.range(Math.ceil(newLastPanelLength / maxPanelLength)).forEach(() => {
        _.last(line.panels).length = Math.min(newLastPanelLength, maxPanelLength);
        newLastPanelLength -= maxPanelLength;

        if (newLastPanelLength > 0) {
          line.panels.push(createPanel(1, line.width));
        }
      });
    } else if (currentLineLength > value) {
      let length = 0;
      line.panels.forEach((p, i) => {
        length += p.length;
        if (length >= value) {
          line.panels.length = i + 1;
          p.length = Math.max(value - (length - p.length), 0);
        }
      });
    }
  }

  static checkPanelsAdjustment(panels: CountertopPanel[], delta: number, line: CountertopLine): void {
    for (const p of panels) {
      if (p.length + delta < 0) {
        throw new Error(
          $localize`Невозможно установить длину для линии. Так как часть ${p.length}×${p.width} не может быть меньше 0`
        );
      }

      if (p.length + delta > line.material.height) {
        throw new Error(
          $localize`Невозможно установить длину для линии. Так как часть ${p.length}×${p.width} не может быть больше длины материала (${line.material.height})`
        );
      }
    }
  }

  static splitPanel(
    countertop: Countertop,
    line: CountertopLine,
    panel: CountertopPanel,
    connector: ClampJoint,
    partsCount = 2,
    direction: 'after' | 'before' = 'after'
  ): void {
    const totalPanelsLength = panel.length;
    const partHeight = totalPanelsLength / partsCount;

    CountertopUtils.setPanelLength(partHeight, panel, line, PanelLengthChangeDirection.None);

    let leftConnectorIndex = line.panelsJoints.findIndex(j => j.panel2.uid === panel.uid);

    let current = panel;
    for (let i = 1; i < partsCount; i++) {
      const newPanel = line.addPanel(partHeight, panel.uid, direction);

      const joint = {
        panel1: direction === 'after' ? current : newPanel,
        panel2: direction === 'after' ? newPanel : current,
        connector: undefined,
      };
      line.panelsJoints.splice(++leftConnectorIndex, 0, joint);
      recalculatePanelJoint(countertop, joint, connector);

      if (direction === 'after') {
        line.panelsJoints[leftConnectorIndex + 1] && (line.panelsJoints[leftConnectorIndex + 1].panel1 = newPanel);
      } else {
        line.panelsJoints[leftConnectorIndex - 1] && (line.panelsJoints[leftConnectorIndex - 1].panel2 = newPanel);
      }

      current = newPanel;
    }
  }

  static setPanelLength(
    value: number,
    panel: CountertopPanel,
    line: CountertopLine,
    direction: PanelLengthChangeDirection = PanelLengthChangeDirection.None
  ): void {
    const material = line.material;
    const panels = line.panels;
    const panelIndex = panels.indexOf(panel);
    const maxPanelLength = material.height;
    let increase = value - panel.length;

    if (direction === PanelLengthChangeDirection.LeftRight) {
      increase /= 2;
    }

    if (panelIndex === -1) {
      throw new Error($localize`Не удалось найти часть линии`);
    }

    if (value < 0) {
      throw new Error($localize`Длина части линии не должна быть меньше ноля`);
    }

    const rightPanelsHeightSum = _.sumBy(panels.slice(panelIndex + 1), p => p.length);
    if (
      (direction === PanelLengthChangeDirection.Right || direction === PanelLengthChangeDirection.LeftRight) &&
      increase > rightPanelsHeightSum
    ) {
      throw new Error($localize`Увеличение больше чем на сумму элементов справа`);
    }

    const leftPanelsHeightSum = _.sumBy(panels.slice(0, panelIndex), p => p.length);
    if (
      (direction === PanelLengthChangeDirection.Left || direction === PanelLengthChangeDirection.LeftRight) &&
      increase > leftPanelsHeightSum
    ) {
      throw new Error($localize`Увеличение больше чем на сумму элементов слева`);
    }

    if (direction === PanelLengthChangeDirection.None) {
      panel.length = value;
    }

    if (direction === PanelLengthChangeDirection.Right || direction === PanelLengthChangeDirection.LeftRight) {
      let currentIndex = panelIndex;
      let remainingIncrease = increase;

      currentIndex++;
      while (remainingIncrease !== 0 && currentIndex < panels.length) {
        const availableIncrease = panels[currentIndex].length;
        if (remainingIncrease >= availableIncrease) {
          remainingIncrease -= availableIncrease;
          panels.splice(currentIndex, 1);
        } else {
          panels[currentIndex].length -= remainingIncrease;
          remainingIncrease = 0;
        }
      }

      panel.length = value;
    }

    if (direction === PanelLengthChangeDirection.Left || direction === PanelLengthChangeDirection.LeftRight) {
      let currentIndex = panelIndex - 1;
      let remainingIncrease = increase;

      panel.length = value;

      while (remainingIncrease !== 0 && currentIndex >= 0) {
        const availableDecrease = panels[currentIndex].length;
        if (remainingIncrease >= availableDecrease) {
          remainingIncrease -= availableDecrease;
          panels.splice(currentIndex, 1);
          currentIndex--;
        } else {
          panels[currentIndex].length -= remainingIncrease;
          remainingIncrease = 0;
        }
      }
    }

    if (
      direction === PanelLengthChangeDirection.Right ||
      direction === PanelLengthChangeDirection.LeftRight ||
      direction === PanelLengthChangeDirection.None
    ) {
      for (let i = 0; i < panels.length; i++) {
        if (panels[i].length > maxPanelLength) {
          const increase = panels[i].length - maxPanelLength;
          panels[i].length = maxPanelLength;
          line.addPanel(increase, panels[i].uid, 'after');
        }
      }
    }

    if (direction === PanelLengthChangeDirection.Left) {
      let i = panels.length - 1;
      while (i >= 0) {
        if (panels[i].length > maxPanelLength) {
          const increase = panels[i].length - maxPanelLength;
          panels[i].length = maxPanelLength;
          line.addPanel(increase, panels[i].uid, 'before');
        } else {
          i--;
        }
      }
    }
  }

  static removePanel(
    countertop: Countertop,
    line: CountertopLine,
    panels: CountertopPanel[],
    panel: CountertopPanel,
    saveLength = true
  ): number {
    if (!panel) {
      throw new Error($localize`Панель не найдена`);
    }

    if (panels.length <= 1) {
      throw new Error($localize`Не возможно удалить единственную часть`);
    }

    const panelIndex = panels.findIndex(p => p.uid === panel.uid);

    if (panelIndex < 0) {
      throw new Error($localize`Панель не найдена`);
    }

    const sizeChangeDirection: PanelLengthChangeDirection = saveLength
      ? panelIndex === 0
        ? PanelLengthChangeDirection.Right
        : PanelLengthChangeDirection.Left
      : PanelLengthChangeDirection.None;

    const connectorIndex = line.panelsJoints.findIndex(pj =>
      panelIndex === 0 ? pj.panel1.uid === panel.uid : pj.panel2.uid === panel.uid
    );

    if (connectorIndex >= 0) {
      _.pullAt(line.panelsJoints, connectorIndex);

      const panelIndex = panels.findIndex(p => p.uid === panel.uid);
      const prevPanelIndex = panelIndex - 1;
      const nextPanelIndex = panelIndex + 1;

      if (line.panelsJoints[connectorIndex] && (prevPanelIndex || nextPanelIndex)) {
        line.panelsJoints[connectorIndex].panel1 = panels[panelIndex === 0 ? nextPanelIndex : prevPanelIndex];
      }
    }

    countertop.setPanelLength(0, line.uid, panel.uid, sizeChangeDirection);
    return _.pull(panels, panel).length;
  }
}
